import {
  defaultGradient,
  gradientCss,
  type GradientPaint,
  type GradientStop,
} from "../native/gradient";

export class NativeGradientEditor {
  active = false;
  private value = defaultGradient("#000000");
  private selected = 0;
  readonly panel = document.createElement("div");
  constructor(
    private changed: (paint: GradientPaint, commit: boolean) => void,
    private stopChanged: (stop: GradientStop) => void,
    private opacityChanged: (alpha: number, commit: boolean) => void,
  ) {
    this.panel.dataset.colorPanel = "gradient";
    this.panel.className = "irl-gradient-editor";
    this.panel.hidden = true;
    this.panel.innerHTML = `
      <label>Непрозрачность, % <input id="irl-gradient-opacity" type="number" min="0" max="100" step="1" value="100"></label>
      <input id="irl-gradient-opacity-range" type="range" min="0" max="100" step="1" aria-label="Непрозрачность градиента">
      <label>Тип <select id="irl-gradient-type"><option value="linear">Линейный</option><option value="radial">Радиальный</option></select></label>
      <div id="irl-gradient-preview" aria-label="Предпросмотр градиента"></div>
      <div id="irl-gradient-stops" role="group" aria-label="Цветовые точки"></div>
      <div class="irl-gradient-actions"><button type="button" id="irl-gradient-add">+ Точка</button><button type="button" id="irl-gradient-remove">Удалить</button><button type="button" id="irl-gradient-apply">Применить</button></div>
      <label>Положение, % <input id="irl-gradient-offset" type="number" min="0" max="100" step="1"></label>
      <input id="irl-gradient-offset-range" type="range" min="0" max="100" step="1" aria-label="Положение точки">
      <label id="irl-gradient-angle-label">Угол, ° <input id="irl-gradient-angle" type="number" min="0" max="360" step="1"></label>
      <input id="irl-gradient-angle-range" type="range" min="0" max="360" step="1" aria-label="Угол градиента">
      <div class="irl-gradient-stop-caption">Цвет и прозрачность выбранной точки</div>`;
    for (const suffix of ["", "-range"]) {
      const input = this.el<HTMLInputElement>("opacity" + suffix);
      const change = (commit: boolean) => {
        if (!input.value || !Number.isFinite(input.valueAsNumber)) return;
        const alpha = Math.max(0, Math.min(100, input.valueAsNumber));
        this.el<HTMLInputElement>("opacity" + (suffix ? "" : "-range")).value =
          String(alpha);
        this.opacityChanged(alpha / 100, commit);
      };
      input.oninput = () => change(false);
      input.onchange = () => change(true);
    }
    this.el<HTMLSelectElement>("type").onchange = () => {
      delete this.value.coordinates;
      this.value.type = this.el<HTMLSelectElement>("type")
        .value as GradientPaint["type"];
      this.emit(true);
    };
    for (const field of ["offset", "angle"] as const)
      for (const suffix of ["", "-range"]) {
        const input = this.el<HTMLInputElement>(field + suffix);
        const update = (commit: boolean) => {
          if (!input.value || !Number.isFinite(input.valueAsNumber)) return;
          const n = Math.max(
            0,
            Math.min(field === "angle" ? 360 : 100, input.valueAsNumber),
          );
          if (field === "angle") {
            this.value.angle = n;
            delete this.value.coordinates;
          } else {
            const stop = this.value.stops[this.selected]!;
            stop.offset = n / 100;
            this.value.stops.sort((a, b) => a.offset - b.offset);
            this.selected = this.value.stops.indexOf(stop);
          }
          this.emit(commit);
        };
        input.oninput = () => update(false);
        input.onchange = () => update(true);
      }
    this.el("add").onclick = () => {
      if (this.value.stops.length >= 16) return;
      let i = 0;
      for (let j = 1; j < this.value.stops.length - 1; j++)
        if (
          this.value.stops[j + 1]!.offset - this.value.stops[j]!.offset >
          this.value.stops[i + 1]!.offset - this.value.stops[i]!.offset
        )
          i = j;
      const a = this.value.stops[i]!,
        b = this.value.stops[i + 1]!;
      const color =
        "#" +
        [1, 3, 5]
          .map((k) =>
            Math.round(
              (parseInt(a.color.slice(k, k + 2), 16) +
                parseInt(b.color.slice(k, k + 2), 16)) /
                2,
            )
              .toString(16)
              .padStart(2, "0"),
          )
          .join("");
      this.value.stops.splice(i + 1, 0, {
        offset: (a.offset + b.offset) / 2,
        color,
        alpha: (a.alpha + b.alpha) / 2,
      });
      this.selected = i + 1;
      this.select(this.selected);
      this.emit(true);
    };
    this.el("remove").onclick = () => {
      if (this.value.stops.length <= 2) return;
      this.value.stops.splice(this.selected, 1);
      this.select(Math.min(this.selected, this.value.stops.length - 1));
      this.emit(true);
    };
    this.el("apply").onclick = () => this.emit(true);
  }
  private el<T extends HTMLElement = HTMLElement>(id: string) {
    return this.panel.querySelector<T>("#irl-gradient-" + id)!;
  }
  open(gradient: GradientPaint | null, color: string, alpha: number) {
    this.value = structuredClone(gradient ?? defaultGradient(color));
    this.selected = 0;
    this.syncOpacity(alpha);
    this.render();
  }
  /** Reflect the owner paint's opacity without resetting stops or their selection. */
  syncOpacity(alpha: number) {
    this.el<HTMLInputElement>("opacity").value = String(
      Math.round(alpha * 100),
    );
    this.el<HTMLInputElement>("opacity-range").value = String(
      Math.round(alpha * 100),
    );
  }
  select(index = this.selected) {
    this.selected = index;
    this.stopChanged(this.value.stops[index]!);
    this.render();
  }
  color(color: string, alpha: number, commit: boolean) {
    Object.assign(this.value.stops[this.selected]!, { color, alpha });
    this.emit(commit);
  }
  private emit(commit: boolean) {
    this.changed(structuredClone(this.value), commit);
    this.render();
  }
  private render() {
    this.el("preview").style.background = gradientCss(this.value);
    this.el<HTMLSelectElement>("type").value = this.value.type;
    this.el("angle-label").hidden = this.value.type !== "linear";
    this.el("angle-range").hidden = this.value.type !== "linear";
    for (const [id, value] of [
      ["angle", this.value.angle],
      ["offset", this.value.stops[this.selected]!.offset * 100],
    ] as const)
      for (const suffix of ["", "-range"]) {
        const input = this.el<HTMLInputElement>(id + suffix);
        if (document.activeElement !== input)
          input.value = String(Math.round(value));
      }
    this.el<HTMLButtonElement>("remove").disabled =
      this.value.stops.length <= 2;
    this.el<HTMLButtonElement>("add").disabled = this.value.stops.length >= 16;
    const stops = this.el("stops");
    while (stops.children.length > this.value.stops.length)
      stops.lastElementChild!.remove();
    this.value.stops.forEach((s, i) => {
      const button =
        (stops.children[i] as HTMLButtonElement | undefined) ??
        document.createElement("button");
      if (!button.parentElement) stops.append(button);
      button.type = "button";
      button.style.background = s.color;
      button.textContent = String(Math.round(s.offset * 100));
      button.style.color =
        parseInt(s.color.slice(1, 3), 16) * 0.299 +
          parseInt(s.color.slice(3, 5), 16) * 0.587 +
          parseInt(s.color.slice(5, 7), 16) * 0.114 >
        140
          ? "#172033"
          : "#fff";
      button.ariaLabel = `Точка ${i + 1}: ${Math.round(s.offset * 100)}%`;
      button.setAttribute("aria-pressed", String(i === this.selected));
      button.onclick = () => this.select(i);
    });
  }
}
